import React, { useState, useEffect } from 'react';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  CircularProgress,
  MenuItem,
  Chip,
  Avatar,
  Checkbox,
  LinearProgress,
  TablePagination,
  Autocomplete
} from '@mui/material';
import { 
  FileDownload as DownloadIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  FileUpload as UploadIcon, 
  LockReset as ResetIcon,
  Search as SearchIcon,
  GroupAdd as GroupAddIcon,
  OpenInNew as LaunchIcon
} from '@mui/icons-material';
import api from '../services/api';
import * as XLSX from 'xlsx';

const StudentManager = () => {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedMajor, setSelectedMajor] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [openImport, setOpenImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  
  // Registrar Import State
  const [openRegistrar, setOpenRegistrar] = useState(false);
  const [registrarPrefix, setRegistrarPrefix] = useState('');
  const [registrarStudents, setRegistrarStudents] = useState<any[]>([]);
  const [loadingRegistrar, setLoadingRegistrar] = useState(false);

  const [formData, setFormData] = useState<any>({
    username: '',
    password: '',
    student_code: '',
    prefix: 'นาย',
    first_name: '',
    last_name: '',
    birthday: '',
    major: '',
    phone_number: '',
    citizen_id: '',
    line_id: '',
    email: '',
    image_url: '',
    group_no: '',
    level1: '',
    faculty_name: '',
    faculty_name_eng: '',
    major_eng: '',
    mjcode: '',
    level_name_long: '',
    level_name_longeng: '',
    level_name: '',
    level_type: '',
    type_name: '',
    date_show_finish: '',
    first_name_eng: '',
    last_name_eng: '',
    address1: '',
    address2: ''
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDateBE = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = (date.getFullYear() + 543).toString().slice(-2);
    return `${d}/${m}/${y}`;
  };

  // Extract unique years from student codes (first 2 digits)
  const uniqueYears = Array.from(new Set(students.map((s: any) => s.student_code.substring(0, 2))))
    .filter(y => y && y.length === 2)
    .sort()
    .reverse();

  // Extract unique student majors directly from active student records in system, seeded with system defaults
  const uniqueMajors = React.useMemo(() => {
    const set = new Set<string>(DEFAULT_STUDENT_MAJORS);
    students.forEach((s: any) => {
      if (s.major && s.major.trim()) {
        set.add(s.major.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'th'));
  }, [students]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/students');
      setStudents(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const filteredStudents = students.filter((s: any) => {
    const studentMajor = (s.major || '').trim();
    const matchesSearch = s.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.student_code.includes(searchTerm) ||
      studentMajor.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesYear = selectedYear === 'ALL' || s.student_code.startsWith(selectedYear);
    const matchesMajor = selectedMajor === 'ALL' || studentMajor === selectedMajor;
    
    return matchesSearch && matchesYear && matchesMajor;
  });

  // Reset page when filtering
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedYear, selectedMajor]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredStudents.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`ยืนยันการย้ายนักศึกษาที่เลือกทั้งหมด ${selected.length} รายการ ไปยังถังขยะ?`)) {
      setLoading(true);
      try {
        await api.post('/students/bulk-soft-delete', { ids: selected });
        setSelected([]);
        fetchStudents();
        alert('ย้ายข้อมูลไปยังถังขยะเรียบร้อยแล้ว');
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการย้ายข้อมูล');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFetchRegistrar = async () => {
    if (registrarPrefix.length !== 7) return alert('กรุณาป้อนรหัส 7 หลัก (เช่น 6611201)');
    setLoadingRegistrar(true);
    setLoading(true);
    setRegistrarStudents([]);
    try {
      const res = await api.get(`/students/registrar/${registrarPrefix}`);
      setRegistrarStudents(res.data);
      if (res.data.length === 0) alert('ไม่พบข้อมูลนักศึกษาในกลุ่มนี้');
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการดึงข้อมูลจากระบบทะเบียน');
    } finally {
      setLoadingRegistrar(false);
      setLoading(false);
    }
  };

  const handleAddRegistrarStudent = async (s: any) => {
    setLoading(true);
    try {
      await api.post('/students', s);
      setRegistrarStudents(prev => prev.map(item => item.student_code === s.student_code ? { ...item, exists: true } : item));
      fetchStudents();
    } catch (err: any) {
      alert(err.response?.data?.message || 'ไม่สามารถเพิ่มข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllRegistrar = async () => {
    const studentsToImport = registrarStudents.filter(s => !s.exists);
    if (studentsToImport.length === 0) return alert('ไม่มีนักศึกษาใหม่ให้นำเข้า');
    setLoading(true);
    try {
      await api.post('/students/bulk', { students: studentsToImport });
      setOpenRegistrar(false);
      setRegistrarStudents([]);
      fetchStudents();
      alert(`นำเข้าข้อมูลนักศึกษาใหม่ ${studentsToImport.length} ราย เรียบร้อยแล้ว`);
    } catch (err) {
      alert('การนำเข้าข้อมูลล้มเหลว');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const exportData = filteredStudents.map((s: any) => ({
      'รหัสนักศึกษา': s.student_code,
      'คำนำหน้า': s.prefix,
      'ชื่อ': s.first_name,
      'นามสกุล': s.last_name,
      'สาขาวิชา': s.major,
      'คณะ': s.faculty_name,
      'หมู่เรียน': s.group_no,
      'เบอร์โทรศัพท์': s.phone_number,
      'อีเมล': s.email,
      'เลขประจำตัวประชาชน': s.citizen_id,
      'วันเกิด': formatDateBE(s.birthday),
      'ที่อยู่ 1': s.address1,
      'ที่อยู่ 2': s.address2
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, `tpms_students_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportCSV = () => {
    const headers = ['รหัสนักศึกษา', 'คำนำหน้า', 'ชื่อ', 'นามสกุล', 'สาขาวิชา', 'เบอร์โทรศัพท์', 'อีเมล', 'Username'];
    const rows = filteredStudents.map((s: any) => [
      s.student_code,
      s.prefix,
      s.first_name,
      s.last_name,
      s.major,
      s.phone_number,
      s.email,
      s.username
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any) => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = async () => {
    if (!importFile) return;
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const text = e.target.result;
      const lines = text.split('\n');
      const studentsToImport = [];
      for (let i = 0; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const parts = lines[i].split(',');
        studentsToImport.push({
            username: parts[0],
            password: parts[1],
            student_code: parts[2],
            prefix: parts[3],
            first_name: parts[4],
            last_name: parts[5],
            major: parts[6],
            phone_number: parts[7],
            citizen_id: parts[8],
            line_id: parts[9],
            email: parts[10],
            image_url: parts[11]
        });
      }
      try {
        setLoading(true);
        await api.post('/students/bulk', { students: studentsToImport });
        setOpenImport(false);
        setImportFile(null);
        fetchStudents();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(importFile);
  };

  const handleResetPassword = async (userId: string, username: string) => {
    const newPassword = window.prompt(`ระบุรหัสผ่านใหม่สำหรับผู้ใช้: ${username}`);
    if (newPassword) {
      setLoading(true);
      try {
        await api.post('/auth/admin-reset-password', { userId, newPassword });
        alert('รีเซ็ตรหัสผ่านเรียบร้อยแล้ว');
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpen = (student: any = null) => {
    if (student) {
      setEditingId(student.id);
      setFormData({
        ...student,
        password: '',
        birthday: student.birthday ? new Date(student.birthday).toISOString().split('T')[0] : ''
      });
    } else {
      setEditingId(null);
      setFormData({
        username: '', password: '', student_code: '', prefix: 'นาย', first_name: '', last_name: '',
        birthday: '', major: '', phone_number: '', citizen_id: '', line_id: '', email: '',
        image_url: '', group_no: '', level1: '', faculty_name: '', faculty_name_eng: '', major_eng: '',
        mjcode: '', level_name_long: '', level_name_longeng: '', level_name: '', level_type: '',
        type_name: '', date_show_finish: '', first_name_eng: '', last_name_eng: '', address1: '', address2: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/students/${editingId}`, formData);
      } else {
        await api.post('/students', formData);
      }
      handleClose();
      fetchStudents();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบข้อมูลนักศึกษา? ระบบจะลบบัญชีผู้ใช้งานด้วย')) {
      setLoading(true);
      try {
        await api.delete(`/students/${id}`);
        fetchStudents();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewFullProfile = () => {
    if (editingId) {
       setSelectedProfile({ ...formData, id: editingId });
       setOpenProfile(true);
    }
  };

  const handleCloseProfile = () => {
    setOpenProfile(false);
    setSelectedProfile(null);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      
      {/* Registrar Integration Section */}
      <Paper sx={{ p: 2, mb: 4, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupAddIcon color="primary" /> ดึงข้อมูลนักศึกษาจากระบบทะเบียน (KPRU Registrar)
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField 
              fullWidth
              size="small"
              label="รหัสหมู่เรียน 7 หลัก"
              placeholder="เช่น 6611201"
              value={registrarPrefix}
              onChange={(e) => setRegistrarPrefix(e.target.value.replace(/[^0-9]/g, '').slice(0, 7))}
              slotProps={{ htmlInput: { maxLength: 7 } }}
              helperText="ระบุรหัส 7 หลักแรกของกลุ่มนักศึกษา"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={loadingRegistrar ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={handleFetchRegistrar}
              disabled={loadingRegistrar || registrarPrefix.length !== 7}
            >
              {loadingRegistrar ? 'กำลังดึงข้อมูล...' : 'ค้นหาในระบบทะเบียน'}
            </Button>
          </Grid>
          {registrarStudents.length > 0 && (
            <Grid size={{ xs: 12, sm: 'auto' }}>
               <Button variant="outlined" color="success" onClick={() => setOpenRegistrar(true)}>
                 ดูผลการค้นหา ({registrarStudents.length} ราย)
               </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h5">จัดการข้อมูลนักศึกษา</Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          width: { xs: '100%', md: 'auto' } 
        }}>
          <TextField
            select
            size="small"
            label="ปีการศึกษา"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
            {uniqueYears.map(year => (
                <MenuItem key={year} value={year}>รหัส {year}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="สาขาวิชา"
            value={selectedMajor}
            onChange={(e) => setSelectedMajor(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="ALL">ทุกสาขาวิชา ({uniqueMajors.length})</MenuItem>
            {uniqueMajors.map(major => (
                <MenuItem key={major} value={major}>{major}</MenuItem>
            ))}
          </TextField>
          <TextField 
            size="small"
            placeholder="ค้นหา..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: { xs: 1, md: 0 }, minWidth: { xs: '100%', sm: 200 } }}
          />
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setOpenImport(true)} size="small">
            นำเข้า
          </Button>
          <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel} size="small">
            ส่งออก Excel
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV} size="small">
            ส่งออก CSV
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleOpen()} size="small" sx={{ flexGrow: { xs: 1, md: 0 } }}>
            เพิ่มนักศึกษา
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflowX: 'auto' }}>
        {selected.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'error.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
              เลือกนักศึกษาแล้ว {selected.length} รายการ
            </Typography>
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<DeleteIcon />} 
              onClick={handleBulkDelete}
              size="small"
            >
              ลบรายการที่เลือก
            </Button>
          </Box>
        )}
        <Table sx={{ minWidth: 1200 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredStudents.length}
                  checked={filteredStudents.length > 0 && selected.length === filteredStudents.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>รหัสนักศึกษา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ-นามสกุล</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>วันเดือนปีเกิด</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>เบอร์โทร</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((student: any) => {
              const isItemSelected = selected.indexOf(student.id) !== -1;
              return (
                <TableRow 
                  key={student.id} 
                  hover 
                  selected={isItemSelected}
                  onClick={() => handleSelectOne(student.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  <TableCell>{student.student_code}</TableCell>
                  <TableCell>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <Avatar src={student.image_url} sx={{ width: 32, height: 32 }} />
                       {student.prefix}{student.first_name} {student.last_name}
                     </Box>
                  </TableCell>
                  <TableCell>{student.major}</TableCell>
                  <TableCell>{formatDateBE(student.birthday)}</TableCell>
                  <TableCell>{student.phone_number || '-'}</TableCell>
                  <TableCell>{student.username}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton color="warning" onClick={() => handleResetPassword(student.user_id, student.username)} title="รีเซ็ตรหัสผ่าน" size="small">
                        <ResetIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={() => handleOpen(student)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(student.id)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredStudents.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  {searchTerm ? 'ไม่พบข้อมูลที่ตรงกับคำค้นหา' : 'ยังไม่มีข้อมูลนักศึกษา'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredStudents.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Import Dialog */}
      <Dialog open={openImport} onClose={() => setOpenImport(false)} fullWidth maxWidth="xs">
        <DialogTitle>นำเข้าข้อมูลนักศึกษา (Bulk Import)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ไฟล์ CSV ต้องมีคอลัมน์ดังนี้: <br/>
            username, password, student_code, prefix, first_name, last_name, major, phone_number, citizen_id, line_id, email, image_url
          </Typography>
          <input 
            type="file" 
            accept=".csv" 
            onChange={(e) => setImportFile(e.target.files ? e.target.files[0] : null)} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImport(false)}>ยกเลิก</Button>
          <Button onClick={handleImportCSV} variant="contained" color="primary" disabled={!importFile}>
            ยืนยันการนำเข้า
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingId ? 'แก้ไขข้อมูลนักศึกษา' : 'เพิ่มข้อมูลนักศึกษา'}
          {editingId && (
            <Button 
              size="small" 
              startIcon={<LaunchIcon />} 
              onClick={handleViewFullProfile}
              variant="outlined"
            >
              โปรไฟล์เต็ม
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar 
              src={formData.image_url} 
              sx={{ width: 100, height: 100, border: '2px solid', borderColor: 'primary.main' }}
            />
          </Box>
          <Grid container spacing={2}>
            {!editingId && (
              <>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="เลขประจำตัวบัตรประชาชน"
                name="citizen_id"
                value={formData.citizen_id}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField
                fullWidth
                select
                label="คำนำหน้า"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                slotProps={{ select: { native: true } }}
              >
                <option value="นาย">นาย</option>
                <option value="นาง">นาง</option>
                <option value="นางสาว">นางสาว</option>
                <option value="ดร.">ดร.</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 8, sm: 5 }}>
              <TextField
                fullWidth
                label="ชื่อ"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                label="นามสกุล"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="date"
                label="วัน/เดือน/ปีเกิด"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="รหัสนักศึกษา"
                name="student_code"
                value={formData.student_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Autocomplete
                freeSolo
                options={uniqueMajors}
                value={formData.major || ''}
                onChange={(event, newValue) => {
                  setFormData((prev: any) => ({ ...prev, major: newValue || '' }));
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData((prev: any) => ({ ...prev, major: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="สาขาวิชา"
                    name="major"
                    helperText="เลือกจากสาขาที่มีในระบบ หรือพิมพ์ระบุสาขาใหม่"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Line ID"
                name="line_id"
                value={formData.line_id}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="URL รูปภาพนักศึกษา"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Registrar Results Dialog */}
      <Dialog open={openRegistrar} onClose={() => setOpenRegistrar(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          รายชื่อนักศึกษาจากระบบทะเบียน (รหัสกลุ่ม: {registrarPrefix})
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>รหัสนักศึกษา</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>รูปภาพ</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>คำนำหน้า</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ-นามสกุล</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrarStudents.map((s) => (
                  <TableRow key={s.student_code} hover>
                    <TableCell>{s.student_code}</TableCell>
                    <TableCell>
                      <Avatar src={s.image_url} variant="rounded" sx={{ width: 40, height: 48 }} />
                    </TableCell>
                    <TableCell>{s.prefix}</TableCell>
                    <TableCell>{s.first_name} {s.last_name}</TableCell>
                    <TableCell>{s.major}</TableCell>
                    <TableCell align="center">
                      {s.exists ? (
                        <Chip label="มีในระบบแล้ว" size="small" variant="outlined" color="default" />
                      ) : (
                        <Button size="small" variant="contained" color="primary" onClick={() => handleAddRegistrarStudent(s)}>เพิ่ม</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenRegistrar(false)}>ปิด</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<GroupAddIcon />}
            onClick={handleAddAllRegistrar}
            disabled={registrarStudents.filter(s => !s.exists).length === 0}
          >
            เพิ่มนักศึกษาใหม่ทั้งหมด ({registrarStudents.filter(s => !s.exists).length} ราย)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Profile View Dialog */}
      <Dialog open={openProfile} onClose={handleCloseProfile} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LaunchIcon /> รายละเอียดโปรไฟล์นักศึกษา (Full Profile)
        </DialogTitle>
        <DialogContent dividers>
          {selectedProfile && (
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center' }}>
                <Avatar 
                  src={selectedProfile.image_url} 
                  sx={{ width: 180, height: 220, margin: '0 auto', borderRadius: 2, border: '4px solid #eee', boxShadow: 2 }}
                  variant="rounded"
                />
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                  {selectedProfile.prefix}{selectedProfile.first_name} {selectedProfile.last_name}
                </Typography>
                <Typography variant="body1" color="textSecondary">{selectedProfile.first_name_eng} {selectedProfile.last_name_eng}</Typography>
                <Typography color="primary" sx={{ fontWeight: 'bold', mt: 1 }}>{selectedProfile.student_code}</Typography>
                <Chip label={selectedProfile.type_name || "นักศึกษาปกติ"} color="secondary" size="small" sx={{ mt: 1 }} />
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 0.5 }}>
                  ข้อมูลวิชาการ
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">คณะ (TH)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.faculty_name || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">Faculty (EN)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.faculty_name_eng || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">สาขาวิชา (TH)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.major || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">Major (EN)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.major_eng || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">วุฒิการศึกษา</Typography>
                    <Typography variant="body2">{selectedProfile.level_name_long || '-'}</Typography>
                    <Typography variant="caption" color="textSecondary">{selectedProfile.level_name_longeng || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">หมู่เรียน / หลักสูตร</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.group_no || '-'} ({selectedProfile.level_name || '-'})</Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', mt: 4, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 0.5 }}>
                  ข้อมูลส่วนตัวและที่อยู่
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">เลขประจำตัวประชาชน</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.citizen_id || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">วันเดือนปีเกิด</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateBE(selectedProfile.birthday)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">เบอร์โทรศัพท์</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.phone_number || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="textSecondary">อีเมล</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedProfile.email || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">ที่อยู่</Typography>
                    <Typography variant="body2">{selectedProfile.address1 || '-'}</Typography>
                    <Typography variant="body2">{selectedProfile.address2 || ''}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfile} variant="contained">ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentManager;
